import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, message, Spin, Alert } from 'antd';
import { SendOutlined, UserOutlined, MailOutlined, WarningOutlined, CheckCircleOutlined, BellOutlined, AlertOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import clientService from '../../../services/Clients_service';
import { sendMail } from '../../../services/Mail_service';
import axios from '../../../api/axios';

interface AdminReminderModalProps {
  visible: boolean;
  onClose: () => void;
  currentAnnonce: any;
  onSuccess?: () => void;
}

type SendingStatus = 'idle' | 'sending' | 'success' | 'error';

const AdminReminderModal: React.FC<AdminReminderModalProps> = ({
  visible,
  onClose,
  currentAnnonce,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingClientData, setLoadingClientData] = useState(false);
  const [sendingStatus, setSendingStatus] = useState<SendingStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [currentReminderStep, setCurrentReminderStep] = useState<number>(1);

  useEffect(() => {
    if (visible && currentAnnonce) {
      form.setFieldsValue({
        userNom: currentAnnonce.firstname || '',
        userEmail: currentAnnonce.email || ''
      });

      // Déterminer l'étape actuelle basée sur reminderCount
      const reminderCount = currentAnnonce.reminderCount || 0;
      const nextStep = Math.min(reminderCount + 1, 3);
      setCurrentReminderStep(nextStep);

      fetchClientData();
    }
    
    if (!visible) {
      form.resetFields();
      setSendingStatus('idle');
      setErrorMessage('');
      setCurrentReminderStep(1);
    }
  }, [visible, currentAnnonce]);

  const fetchClientData = async () => {
    if (!currentAnnonce?.clientId) {
      form.setFieldsValue({
        clientNom: '',
        clientEmail: ''
      });
      return;
    }

    setLoadingClientData(true);
    try {
      const clientData = await clientService.getClientById(Number(currentAnnonce.clientId));
      const clientName = clientService.formatClientForDisplay(clientData);

      form.setFieldsValue({
        clientNom: clientName,
        clientEmail: clientData.email || ''
      });
    } catch (error: any) {
      console.error('Erreur lors de la récupération des données client:', error);
      message.warning(error.message || 'Impossible de récupérer les informations du client');
      form.setFieldsValue({
        clientNom: '',
        clientEmail: ''
      });
    } finally {
      setLoadingClientData(false);
    }
  };

  // Génération du contenu mail selon l'étape
  const generateClientMailContent = (step: number, values: any) => {
    const numAnnonce = currentAnnonce.numAnnonce || currentAnnonce.num_annonce || 'N/A';
    const montantTTC = currentAnnonce.montantTTC || currentAnnonce.montant_ttc || '0';
    const nomJal = currentAnnonce.nomJal || 'N/A';
    const typeAnnonce = currentAnnonce.type_annonce || 'N/A';
    const lienPaiement = currentAnnonce.lienPaiement || '#';

    const details = `
      <div style="margin: 20px 0; padding: 15px; background: #f9f9f9; border-left: 4px solid #eb8635;">
        <p style="margin: 5px 0;"><strong>Numéro d'annonce :</strong> ${numAnnonce}</p>
        <p style="margin: 5px 0;"><strong>Montant TTC :</strong> ${montantTTC} €</p>
        <p style="margin: 5px 0;"><strong>Support :</strong> ${nomJal}</p>
        <p style="margin: 5px 0;"><strong>Type :</strong> ${typeAnnonce}</p>
        <p style="margin: 5px 0;"><strong>Lien de paiement :</strong> <a href="${lienPaiement}" style="color: #eb8635; text-decoration: none;">Cliquez ici pour payer</a></p>
      </div>
    `;

    if (step === 1) {
      return `
        <p>Bonjour,</p>
        <p>Votre cabinet conseil a procédé à la demande de publication de votre annonce légale auprès de notre service <strong>ALO - Annonce Légale Officielle</strong>.</p>
        <p>Nous vous rappelons que le règlement correspondant reste à effectuer afin de permettre la diffusion définitive de l'annonce et la remise de l'attestation officielle.</p>
        ${details}
        <p>Nous vous remercions par avance de bien vouloir procéder au paiement via le lien sécurisé transmis précédemment.</p>
        <p>Bien cordialement,<br/>
        <strong>L'équipe ALO</strong><br/>
        <em>Annonce Légale Officielle – ALO-FR.fr</em></p>
      `;
    } else if (step === 2) {
      return `
        <p>Bonjour,</p>
        <p>Nous revenons vers vous concernant le règlement de la facture relative à la publication de votre annonce légale transmise par votre cabinet conseil.</p>
        <p>À ce jour, nous ne constatons toujours pas la réception du paiement correspondant. Nous vous invitons à régulariser la situation dès que possible afin de clôturer le dossier comptable.</p>
        ${details}
        <p>Nous vous remercions de votre diligence.</p>
        <p>Cordialement,<br/>
        <strong>L'équipe ALO</strong><br/>
        <em>Annonce Légale Officielle – ALO-FR.fr</em></p>
      `;
    } else {
      // Étape 3 et suivantes
      return `
        <p>Bonjour,</p>
        <p>Malgré nos précédentes relances, la facture relative à votre annonce légale reste à ce jour impayée.</p>
        <p>Nous vous invitons à procéder au règlement <strong>sous 48 heures</strong>, sans quoi le dossier sera transmis à notre service de gestion pour traitement administratif.</p>
        ${details}
        <p>Nous comprenons que cet oubli puisse résulter d'une simple inattention et restons disponibles en cas de difficulté de paiement.</p>
        <p>Nous vous remercions de régulariser rapidement la situation afin d'éviter toute mesure complémentaire.</p>
        <p>Très cordialement,<br/>
        <strong>L'équipe ALO</strong><br/>
        <em>Annonce Légale Officielle – ALO-FR.fr</em></p>
      `;
    }
  };

  const generateClientMailSubject = (step: number) => {
    const numAnnonce = currentAnnonce.numAnnonce || currentAnnonce.num_annonce || 'N/A';
    
    if (step === 1) {
      return `Rappel de règlement - Annonce légale en attente de paiement (N°${numAnnonce})`;
    } else if (step === 2) {
      return `Relance - Facture en attente de règlement (N°${numAnnonce})`;
    } else {
      return `Dernier rappel avant mise en gestion - Facture impayée (N°${numAnnonce})`;
    }
  };

  const generateUserMailContent = (step: number, values: any) => {
    const numAnnonce = currentAnnonce.numAnnonce || currentAnnonce.num_annonce || 'N/A';
    const clientName = values.clientNom || 'votre client';
    const montantTTC = currentAnnonce.montantTTC || currentAnnonce.montant_ttc || '0';
    const nomJal = currentAnnonce.nomJal || 'N/A';
    const typeAnnonce = currentAnnonce.type_annonce || 'N/A';

    const details = `
      <div style="margin: 20px 0; padding: 15px; background: #f0f9ff; border-left: 4px solid #2A919D;">
        <p style="margin: 5px 0;"><strong>Client :</strong> ${clientName}</p>
        <p style="margin: 5px 0;"><strong>Numéro d'annonce :</strong> ${numAnnonce}</p>
        <p style="margin: 5px 0;"><strong>Montant TTC :</strong> ${montantTTC} €</p>
        <p style="margin: 5px 0;"><strong>Support :</strong> ${nomJal}</p>
        <p style="margin: 5px 0;"><strong>Type :</strong> ${typeAnnonce}</p>
      </div>
    `;

    if (step === 1) {
      return `
        <p>Chers clients,</p>
        <p>Nous vous informons que la facture relative à l'annonce légale transmise par votre cabinet a bien été adressée à votre client : <strong>${clientName}, annonce N°${numAnnonce}</strong>. Le dossier est désormais en attente de règlement.</p>
        ${details}
        <p>Vous pourrez suivre l'avancement directement depuis votre tableau de bord ALO, au sein de la fiche du client concerné : le statut passera automatiquement de "Non payé" à "Payé" dès validation du règlement.</p>
        <p>Nous restons à votre disposition en cas de besoin ou de question complémentaire.</p>
        <p>Bien cordialement,<br/>
        <strong>L'équipe ALO</strong><br/>
        <em>Annonce Légale Officielle – ALO-FR.fr</em></p>
      `;
    } else if (step === 2) {
      return `
        <p>Chers clients,</p>
        <p>Nous revenons vers vous concernant le dossier transmis pour publication via votre cabinet. Malgré l'envoi de la facture à votre client : <strong>${clientName}, annonce N°${numAnnonce}</strong>, celle-ci n'a toujours pas été réglée à ce jour.</p>
        ${details}
        <p>Nous vous remercions par avance de bien vouloir, si possible, inviter votre client à procéder au paiement, afin de permettre la clôture définitive du dossier et la remise de l'attestation définitive.</p>
        <p>Nous restons à votre disposition pour toute précision complémentaire, et vous remercions sincèrement pour votre confiance renouvelée.</p>
        <p>Très cordialement,<br/>
        <strong>L'équipe ALO</strong><br/>
        <em>Annonce Légale Officielle – ALO-FR.fr</em></p>
      `;
    } else {
      // Étape 3 et suivantes
      return `
        <p>Chers clients,</p>
        <p>Malgré nos précédentes relances adressées à votre client : <strong>${clientName}, annonce N°${numAnnonce}</strong>, la facture liée à l'annonce légale transmise par votre cabinet demeure impayée.</p>
        ${details}
        <p>Nous nous permettons de revenir vers vous afin de vous en informer et, si possible, d'obtenir votre aide pour relancer votre client en vue de la régularisation du paiement.</p>
        <p>Nous vous remercions pour votre coopération et restons naturellement à votre disposition pour tout suivi complémentaire.</p>
        <p>Avec nos salutations les plus distinguées,<br/>
        <strong>L'équipe ALO</strong><br/>
        <em>Annonce Légale Officielle – ALO-FR.fr</em></p>
      `;
    }
  };

  const generateUserMailSubject = (step: number) => {
    const numAnnonce = currentAnnonce.numAnnonce || currentAnnonce.num_annonce || 'N/A';
    
    if (step === 1) {
      return `Envoi de la facture à votre client – Annonce légale transmise (N°${numAnnonce})`;
    } else if (step === 2) {
      return `Rappel - Facture client toujours en attente de règlement (N°${numAnnonce})`;
    } else {
      return `Dernier rappel – Facture non réglée par votre client (N°${numAnnonce})`;
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setSendingStatus('sending');
    setErrorMessage('');
    
    try {
      const annonceId = currentAnnonce?.annonce_id || currentAnnonce?.id;
      if (!annonceId) {
        throw new Error('ID de l\'annonce introuvable');
      }

      const mailPromises: Promise<any>[] = [];

      // 📧 Mail utilisateur
      mailPromises.push(
        sendMail({
          name: values.userNom,
          email: values.userEmail,
          object: generateUserMailSubject(currentReminderStep),
          message: generateUserMailContent(currentReminderStep, values),
          sender: undefined,
          bcc: [],
          allowUnsubscribe: false,
        })
      );

      // 📧 Mail client (si présent)
      if (values.clientEmail && values.clientNom) {
        mailPromises.push(
          sendMail({
            name: values.clientNom,
            email: values.clientEmail,
            object: generateClientMailSubject(currentReminderStep),
            message: generateClientMailContent(currentReminderStep, values),
            sender: undefined,
            bcc: [],
            allowUnsubscribe: false,
          })
        );
      }

      const results = await Promise.allSettled(mailPromises);
      const successes = results.filter(r => r.status === 'fulfilled').length;
      const failures = results.filter(r => r.status === 'rejected').length;

      console.log('Résultats envoi mail:', results);

      if (failures > 0) {
        results.forEach((r, idx) => {
          if (r.status === 'rejected') {
            console.error(`Mail #${idx} échoué:`, (r as PromiseRejectedResult).reason);
          }
        });
        throw new Error(`${successes} mail(s) envoyé(s) — ${failures} échec(s)`);
      }

      // 🔹 Mise à jour du compteur en base de données
      try {
        const resp = await axios.post(`/annonce/${annonceId}/increment-reminder`, {
          reminderType: 'admin',
          reminderStep: currentReminderStep,
          userEmail: values.userEmail,
          clientEmail: values.clientEmail || null
        });

        console.log('Réponse incrément reminder:', resp?.data);
      } catch (dbError: any) {
        console.error('Erreur lors de la mise à jour du compteur:', dbError?.response?.data || dbError);
        message.warning('Les mails ont été envoyés, mais la mise à jour du compteur a échoué');
      }

      // 🎉 Succès
      setSendingStatus('success');
      message.success(`Relance ${currentReminderStep} envoyée avec succès !`);
      
      if (onSuccess) onSuccess();
      
      // Fermeture après 2s
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (error: any) {
      console.error('Erreur lors de l\'envoi de la relance:', error);
      setSendingStatus('error');
      setErrorMessage(error.message || 'Erreur lors de l\'envoi de la relance');
      message.error(error.message || 'Erreur lors de l\'envoi de la relance');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSendingStatus('idle');
    setErrorMessage('');
    onClose();
  };

  // Affichage de l'étape actuelle
  const renderReminderStepBadge = () => {
    const stepConfig = {
      1: {
        color: '#4BB0C0',
        bg: '#e6f7f9',
        icon: <BellOutlined style={{ fontSize: '24px', color: '#4BB0C0' }} />,
        label: 'Première relance'
      },
      2: {
        color: '#2A919D',
        bg: '#e6f4f5',
        icon: <AlertOutlined style={{ fontSize: '24px', color: '#2A919D' }} />,
        label: 'Deuxième relance'
      },
      3: {
        color: '#eb8635',
        bg: '#fff9f0',
        icon: <ExclamationCircleOutlined style={{ fontSize: '24px', color: '#eb8635' }} />,
        label: 'Dernière relance (Avant mise en gestion)'
      }
    };

    const config = stepConfig[currentReminderStep as keyof typeof stepConfig];

    return (
      <div style={{
        padding: '16px',
        background: config.bg,
        borderRadius: '8px',
        marginBottom: '24px',
        border: `2px solid ${config.color}`,
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px solid ${config.color}`
          }}>
            {config.icon}
          </div>
          <div>
            <div style={{
              fontSize: '16px',
              fontWeight: 600,
              color: config.color
            }}>
              {config.label}
            </div>
            {/* {currentAnnonce?.reminderCount > 0 && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {currentAnnonce.reminderCount} relance(s) déjà envoyée(s)
              </div>
            )} */}
          </div>
        </div>
      </div>
    );
  };

  // Affichage du statut d'envoi
  const renderSendingStatus = () => {
    if (sendingStatus === 'idle') return null;

    if (sendingStatus === 'sending') {
      return (
        <Alert
          message="Envoi en cours..."
          description="Les mails de relance sont en cours d'envoi. Veuillez patienter."
          type="info"
          showIcon
          icon={<LoadingOutlined />}
          style={{ marginBottom: '24px' }}
        />
      );
    }

    if (sendingStatus === 'success') {
      return (
        <Alert
          message="Relance envoyée avec succès !"
          description={`La relance ${currentReminderStep} a été envoyée à l'utilisateur${form.getFieldValue('clientEmail') ? ' et au client' : ''}.`}
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          style={{ marginBottom: '24px' }}
        />
      );
    }

    if (sendingStatus === 'error' && errorMessage) {
      return (
        <Alert
          message="Erreur lors de l'envoi"
          description={errorMessage}
          type="error"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: '24px' }}
        />
      );
    }

    return null;
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SendOutlined style={{ color: '#eb8635' }} />
          <span>Relancer le paiement</span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={650}
      destroyOnClose
    >
      <Spin spinning={loadingClientData}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Badge de l'étape actuelle */}
          {renderReminderStepBadge()}

          {/* Statut d'envoi */}
          {renderSendingStatus()}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            {/* Utilisateur (cabinet) */}
            <div style={{
              background: '#f5f5f5',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#2A919D', fontSize: '14px', fontWeight: 600 }}>
                <UserOutlined style={{ marginRight: '8px' }} />
                Destinataire Utilisateur (Cabinet)
              </h4>

              <Form.Item
                name="userNom"
                label="Nom de l'utilisateur"
                rules={[{ required: true, message: 'Le nom est obligatoire' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Nom de l'utilisateur" />
              </Form.Item>

              <Form.Item
                name="userEmail"
                label="Email de l'utilisateur"
                rules={[
                  { required: true, message: 'L\'email est obligatoire' },
                  { type: 'email', message: 'Format d\'email invalide' }
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="email@utilisateur.com" />
              </Form.Item>
            </div>

            {/* Client */}
            <div style={{
              background: '#fff9f0',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              border: '1px solid #ffe7ba'
            }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#eb8635', fontSize: '14px', fontWeight: 600 }}>
                <UserOutlined style={{ marginRight: '8px' }} />
                Destinataire Client (Qui doit payer)
              </h4>

              <Form.Item
                name="clientNom"
                label="Nom du client"
                tooltip="Laissez vide si pas de facturation client"
              >
                <Input prefix={<UserOutlined />} placeholder="Nom du client (optionnel)" disabled={loadingClientData} />
              </Form.Item>

              <Form.Item
                name="clientEmail"
                label="Email du client"
                rules={[{ type: 'email', message: 'Format d\'email invalide' }]}
                tooltip="Laissez vide si pas de facturation client"
              >
                <Input prefix={<MailOutlined />} placeholder="email@client.com (optionnel)" disabled={loadingClientData} />
              </Form.Item>

              {!currentAnnonce?.clientId && (
                <div style={{ fontSize: '12px', color: '#999', marginTop: '-8px', fontStyle: 'italic' }}>
                  Aucune facturation client détectée pour cette annonce
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <Button onClick={handleClose} disabled={loading}>Annuler</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SendOutlined />}
                style={{ background: '#eb8635', borderColor: '#eb8635' }}
                disabled={sendingStatus !== 'idle'}
              >
                Envoyer la relance
              </Button>
            </div>
          </Form>

          {/* Info */}
          <div style={{
            marginTop: '24px',
            padding: '12px',
            background: '#f0f9ff',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#666',
            border: '1px solid #bae6fd'
          }}>
            <strong>ℹ️ Information :</strong> Cette relance sera envoyée à l'utilisateur et à son client. Le format du mail s'adapte automatiquement selon l'étape de relance actuelle.
          </div>
        </motion.div>
      </Spin>
    </Modal>
  );
};

export default AdminReminderModal;